// src/production/MasterDesign.js
import React, { useState, useEffect, useMemo } from 'react';
import {productionApi} from './../api'; // Import the productionApi instance
import { useMasterData } from '../../utils/useMasterData';
import { 
  LayoutDashboard, 
  Settings, 
  Plus, 
  Save, 
  X, 
  Edit2, 
  Trash2, 
  ChevronUp, 
  ChevronDown, 
  CheckCircle2, 
  AlertCircle, 
  Package, 
  FileText, 
  Gem, 
  ArrowRight, 
  Grid3x3, 
  Search, 
  Image as ImageIcon, 
  ArrowLeft, 
  Upload, 
  CheckSquare, 
  Square, 
  GripVertical, 
  Check, 
  Layers,
  Loader2,
  ChevronRight,
  Beaker,
  AlertTriangle
} from 'lucide-react';
import { createPortal } from "react-dom";

// --- API CONFIGURATION ---
// In a real project, this would likely be imported from '../api'


// --- CONSTANTS & DEFAULTS ---

const DEFAULT_PRE_CASTING = [
  { stage_code: 'WAX_CREATION', label: 'Wax Creation', qc_required: true, sequence: 1, enabled: true, default_materials: [] },
  { stage_code: 'WAX_CLEANING', label: 'Wax Cleaning', qc_required: true, sequence: 2, enabled: true, default_materials: [] },

  { stage_code: 'WAX_SETTING', label: 'Wax Setting', qc_required: true, sequence: 3, enabled: true, default_materials: [] },
  { stage_code: 'TREE_MAKING', label: 'Tree Making', qc_required: true, sequence: 4, enabled: true, default_materials: [] },
  { stage_code: 'POWDER_MIXING', label: 'Powder Mixing', qc_required: false, sequence: 5, enabled: true, default_materials: [] },
];

const DEFAULT_CASTING = [
  { stage_code: 'MOLDING', label: 'Molding', qc_required: true, sequence: 1, enabled: true, default_materials: [] },
  { stage_code: 'CASTING', label: 'Casting Process', qc_required: false, sequence: 2, enabled: true, default_materials: [] },
];

const DEFAULT_POST_CASTING = [
  { stage_code: 'TREE_CUTTING', label: 'Tree Cutting', qc_required: true, sequence: 1, enabled: true, default_materials: [] },
  { stage_code: 'FILING', label: 'Filing', qc_required: true, sequence: 2, enabled: true, default_materials: [] },
  { stage_code: 'BUFFING', label: 'Buffing', qc_required: true, sequence: 3, enabled: true, default_materials: [] },
  { stage_code: 'ASSEMBLING', label: 'Assembling', qc_required: true, sequence: 4, enabled: true, default_materials: [] },
  { stage_code: 'HAND_SETTING', label: 'Hand Setting', qc_required: true, sequence: 5, enabled: true, default_materials: [] },
  { stage_code: 'POLISHING', label: 'Polishing', qc_required: true, sequence: 6, enabled: true, default_materials: [] },
  { stage_code: 'FINAL_QC', label: 'Final QC', qc_required: true, sequence: 7, enabled: true, default_materials: [] },
];

const NON_PRODUCTION_STAGE_CODES = new Set(['PACKING']);

const normalizeStageCode = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const sanitizeStages = (stages = []) =>
  (stages || [])
    .filter((stage) => !NON_PRODUCTION_STAGE_CODES.has(normalizeStageCode(stage.stage_code || stage.stage_name || stage.label)))
    .map((stage, index) => ({
      ...stage,
      sequence: index + 1
    }));

const sanitizeStageGroups = (stages = {}) => ({
  pre_casting: sanitizeStages(stages.pre_casting || DEFAULT_PRE_CASTING),
  casting: sanitizeStages(stages.casting || DEFAULT_CASTING),
  post_casting: sanitizeStages(stages.post_casting || DEFAULT_POST_CASTING)
});

const normalizeDesignStone = (stone = {}) => ({
  material_id: stone.material_id || stone.id || null,
  name: stone.name || stone.type || '',
  type: stone.type || stone.material_type || stone.name || '',
  material_type: stone.material_type || stone.type || '',
  shape: stone.shape || '',
  size: stone.size || 'N/A',
  count: Number(stone.count || stone.qty || 1),
  quality_grade: stone.quality_grade || 'Standard',
  clarity: stone.clarity || 'N/A',
  cut: stone.cut || '',
  color: stone.color || '',
  unit: stone.unit || 'pcs',
  pieces_available: Number(stone.pieces_available || stone.total_pieces_available || 0),
  total_carat_available: Number(stone.total_carat_available || 0),
});

const buildStonePayloadFromInventory = (stone) => normalizeDesignStone({
  material_id: stone.material_id,
  name: stone.name,
  type: stone.material_type || stone.name,
  material_type: stone.material_type,
  size: stone.size,
  quality_grade: stone.quality_grade,
  clarity: stone.clarity,
  cut: stone.cut,
  color: stone.color,
  unit: stone.unit || 'pcs',
  pieces_available: stone.pieces_available || stone.total_pieces_available || 0,
  total_carat_available: stone.total_carat_available || 0,
  count: 1,
});

const DEFAULT_STONE_TYPE_FILTER = 'PRECIOUS_STONE';
const formatPerUnitLabel = (value) => {
  const normalized = String(value || 'PCS').trim().toUpperCase();
  return normalized === 'GRAM' ? 'Per Gram' : 'Per Piece';
};
const normalizeStoneFilterValue = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const createDefaultPartMaterial = () => ({
  material_id: '',
  material_name: '',
  qty: '',
  unit: '',
});

const createDefaultPart = (index = 0) => ({
  id: `part-${Date.now()}-${index}`,
  name: `Part ${index + 1}`,
  materials: [createDefaultPartMaterial()],
});

const normalizeDesignParts = (parts = []) => {
  if (!Array.isArray(parts) || parts.length === 0) {
    return [createDefaultPart(0)];
  }

  return parts.map((part, index) => ({
    id: part.id || `part-${index + 1}`,
    name: part.name || `Part ${index + 1}`,
    materials: Array.isArray(part.materials) && part.materials.length > 0
      ? part.materials.map((material) => ({
          material_id: material.material_id || '',
          material_name: material.material_name || '',
          qty: material.qty ?? '',
          unit: material.unit || '',
        }))
      : [createDefaultPartMaterial()],
  }));
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// --- COMPONENTS ---

// 1. PRODUCT DETAIL VIEW
const ProductDetailView = ({ design, masterData, onBack, onEdit }) => {
  const targetCostUnitLabel = formatPerUnitLabel(design.dimensions?.target_cost_unit);
  const typicalPriceUnitLabel = formatPerUnitLabel(design.dimensions?.typical_price_unit);
  const fullWorkflow = [
    ...(design.stages?.pre_casting || []),
    ...(design.stages?.casting || []),
    ...(design.stages?.post_casting || [])
  ].sort((a,b) => (a.sequence || 0) - (b.sequence || 0))
   .filter(s => s.enabled !== false); 

  const aggregatedMaterials = useMemo(() => {
    const mats = [];
    const processGroups = ['pre_casting', 'casting', 'post_casting'];
    
    processGroups.forEach(group => {
      if (design.stages && design.stages[group]) {
        design.stages[group].forEach(stage => {
          if (stage.default_materials && stage.default_materials.length > 0) {
            stage.default_materials.forEach(m => {
              mats.push({
                ...m,
                stage_name: stage.label || stage.stage_name, 
                process: group.replace('_', ' ').toUpperCase()
              });
            });
          }
        });
      }
    });
    return mats;
  }, [design]);

  const getCategoryName = (id) => masterData.categories.find(c => c.id === id)?.name || 'Unknown';

  return (
    <div className="bg-white h-full flex flex-col animate-in fade-in slide-in-from-right-8 duration-500 overflow-y-auto">
      <div className="px-8 py-4 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-500 sticky top-0 bg-white z-10">
         <button onClick={onBack} className="hover:text-indigo-600 flex items-center gap-1 transition-colors">
            <ArrowLeft size={16} /> Back to Catalogue
         </button>
         <span>/</span>
         <span className="font-medium text-slate-900">{design.design_code}</span>
         <button onClick={() => onEdit(design)} className="ml-auto flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold text-xs bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors">
            <Edit2 size={14} /> Edit Design
         </button>
      </div>

      <div className="max-w-7xl mx-auto w-full p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
           <div className="space-y-4">
              <div className="aspect-square bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden relative group flex items-center justify-center">
                 {design.image ? (
                    <img 
                      src={design.image} 
                      alt={design.design_name} 
                      onError={(e) => {
                         e.target.onerror = null; 
                         e.target.src = "https://images.unsplash.com/photo-1617038220319-276d3cfab638?auto=format&fit=crop&q=80&w=1000";
                      }}
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-700" 
                    />
                 ) : (
                    <div className="flex flex-col items-center justify-center text-slate-300">
                       <Gem size={80} strokeWidth={1} />
                       <span className="text-sm mt-2 font-medium">No Image Available</span>
                    </div>
                 )}
                 <div className="absolute top-4 left-4">
                     <span className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-sm backdrop-blur-md border border-white/20 ${
                      design.status === 'ACTIVE' 
                        ? 'bg-emerald-500/90 text-white' 
                        : design.status === 'INACTIVE'
                          ? 'bg-slate-500/90 text-white'
                          : 'bg-amber-500/90 text-white'
                    }`}>
                      {design.status}
                    </span>
                 </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                  {[design.image, null, null, null].map((img, i) => (
                    <div key={i} className={`aspect-square rounded-lg border-2 ${i===0 ? 'border-indigo-600' : 'border-slate-100'} overflow-hidden bg-slate-50 flex items-center justify-center cursor-pointer hover:border-indigo-300 transition-colors`}>
                       {img ? (
                          <img src={img} alt={`${design.design_name} thumbnail ${i + 1}`} className="w-full h-full object-cover" 
                           onError={(e) => {e.target.style.display='none'}}
                          />
                       ) : (
                          <ImageIcon size={20} className="text-slate-300" />
                       )}
                    </div>
                  ))}
              </div>
           </div>

           <div className="space-y-8">
              <div>
                 <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">{design.design_name}</h1>
                        <div className="flex items-center gap-3 text-sm text-slate-500 font-medium">
                           <span className="text-indigo-600 font-bold bg-indigo-50 px-2 py-1 rounded border border-indigo-100">{design.design_code}</span>
                           <span className="text-slate-300">|</span>
                           <span>{getCategoryName(design.category_id)}</span>
                        </div>
                    </div>
                 </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                 <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Gross Weight</div>
                    <div className="text-2xl font-mono text-slate-800">{design.actual_weight || design.weight_3d || 0}<span className="text-sm text-slate-400 ml-1">g</span></div>
                 </div>
                 <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Stone Count</div>
                    <div className="text-2xl font-mono text-slate-800">{design.stones ? design.stones.reduce((a,b)=>a+Number(b.count || 0),0) : 0}<span className="text-sm text-slate-400 ml-1">pcs</span></div>
                 </div>
                 <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Target Cost / {targetCostUnitLabel}</div>
                    <div className="text-2xl font-mono text-slate-800">Rs. {design.target_cost_per_piece || 0}</div>
                 </div>
                 {(design.typical_price_min || design.typical_price_max) && (
                 <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Typical Selling Price / {typicalPriceUnitLabel}</div>
                    <div className="text-lg font-mono text-emerald-800">
                      Rs. {design.typical_price_min || 0} – Rs. {design.typical_price_max || 0}
                    </div>
                 </div>
                 )}
              </div>
              
              <div className="prose text-slate-600 leading-relaxed bg-white p-4 border border-slate-100 rounded-lg shadow-sm">
                 <h4 className="text-xs font-bold text-slate-400 uppercase mb-2">Description</h4>
                 <p>{design.description || "Production-ready master design with full specifications and workflow definitions. Compatible with standard casting and setting processes."}</p>
              </div>

              <div>
                 <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Layers size={18} className="text-indigo-500" /> 
                    Workflow Material Requirements
                 </h3>
                 <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
                    <table className="w-full text-sm text-left">
                       <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                          <tr>
                             <th className="px-4 py-3">Material</th>
                             <th className="px-4 py-3">Stage</th>
                             <th className="px-4 py-3 text-right">Qty</th>
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-slate-100">
                          {aggregatedMaterials.map((mat, i) => (
                             <tr key={i} className="hover:bg-slate-50/50">
                                <td className="px-4 py-3 font-medium text-slate-800">
                                    {mat.material_name}
                                    {mat.material_id && <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-indigo-50 text-indigo-600 rounded-full border border-indigo-100">ID: {mat.material_id}</span>}
                                </td>
                                <td className="px-4 py-3 text-slate-500">
                                   <span className="text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200">{mat.stage_name}</span>
                                </td>
                                <td className="px-4 py-3 text-right text-slate-600 font-mono">
                                   {mat.qty} <span className="text-xs text-slate-400">{mat.unit}</span>
                                </td>
                             </tr>
                          ))}
                          {aggregatedMaterials.length === 0 && (
                             <tr><td colSpan="3" className="p-6 text-center text-slate-400 italic">No specific materials defined in workflow stages</td></tr>
                          )}
                       </tbody>
                    </table>
                 </div>
              </div>

              <div>
                 <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Gem size={18} className="text-indigo-500" /> 
                    Stone Configuration
                 </h3>
                 <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm text-left">
                       <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                          <tr>
                             <th className="px-4 py-3">Stone</th>
                             <th className="px-4 py-3">Type</th>
                             <th className="px-4 py-3">Quality</th>
                             <th className="px-4 py-3">Clarity</th>
                             <th className="px-4 py-3">Cut / Color</th>
                             <th className="px-4 py-3">Size</th>
                             <th className="px-4 py-3 text-right">Qty</th>
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-slate-100">
                          {design.stones && design.stones.map((stone, i) => (
                             <tr key={i}>
                                <td className="px-4 py-3 font-medium text-slate-800">{stone.name || stone.type || '-'}</td>
                                <td className="px-4 py-3 text-slate-600">{stone.material_type || stone.type || '-'}</td>
                                <td className="px-4 py-3 text-slate-600">{stone.quality_grade || 'Standard'}</td>
                                <td className="px-4 py-3 text-slate-600">{stone.clarity || 'N/A'}</td>
                                <td className="px-4 py-3 text-slate-600">{[stone.cut, stone.color].filter(Boolean).join(' / ') || '-'}</td>
                                <td className="px-4 py-3 text-slate-600 font-mono">{stone.size || 'N/A'}</td>
                                <td className="px-4 py-3 text-right font-medium text-slate-800">{stone.count}</td>
                             </tr>
                          ))}
                          {(!design.stones || design.stones.length === 0) && <tr><td colSpan="7" className="p-4 text-center text-slate-400 italic">No stones defined</td></tr>}
                       </tbody>
                    </table>
                 </div>
              </div>
           </div>
        </div>
        
        <div className="mt-16 border-t border-slate-200 pt-10">
           <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
             <Settings className="text-indigo-600"/> Production Workflow
           </h3>
           <div className="flex gap-4 overflow-x-auto pb-6 scrollbar-thin scrollbar-thumb-slate-200">
              {fullWorkflow.map((stage, i) => (
                  <div key={i} className="min-w-[200px] p-4 rounded-xl bg-white border border-slate-200 flex flex-col gap-2 relative group hover:border-indigo-300 hover:shadow-md transition-all">
                      <div className="absolute top-2 right-4 text-slate-100 text-5xl font-black opacity-50 z-0 group-hover:text-indigo-50 transition-colors pointer-events-none">{i+1}</div>
                      
                      <div className="flex items-center gap-2 mb-2 z-10">
                         <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-600 transition-colors">{i+1}</div>
                         <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{stage.process_type || 'STAGE'}</div>
                      </div>

                      <div className="font-bold text-slate-800 text-sm leading-tight z-10">{stage.label || stage.stage_name}</div>
                      <div className="text-[10px] text-slate-400 font-mono z-10">{stage.stage_code}</div>
                      
                      <div className="flex gap-2 mt-2 z-10">
                        {stage.qc_required && <span className="text-[10px] uppercase font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">QC</span>}
                        {stage.machine_required && <span className="text-[10px] uppercase font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">Machine</span>}
                      </div>
                      
                      {stage.default_materials && stage.default_materials.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-100 z-10">
                             <p className="text-[10px] font-bold text-slate-500 uppercase mb-1.5 flex items-center gap-1"><Layers size={10}/> Materials:</p>
                             <div className="flex flex-col gap-1">
                                {stage.default_materials.slice(0, 3).map((m, idx) => (
                                   <div key={idx} className="flex justify-between items-center text-[11px] bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                      <span className="text-slate-700 truncate max-w-[100px]" title={m.material_name}>{m.material_name}</span>
                                      <span className="text-slate-400 font-mono">{m.qty}{m.unit}</span>
                                   </div>
                                ))}
                                {stage.default_materials.length > 3 && <span className="text-[10px] text-indigo-500 pl-1">+{stage.default_materials.length - 3} more</span>}
                             </div>
                          </div>
                      )}
                  </div>
              ))}
           </div>
        </div>
      </div>

    </div>
  );
};

const StatusDropdown = ({ status, onChange }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef(null);

  const isActive = status === "ACTIVE";

  const handleToggle = (e) => {
    e.stopPropagation();

    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
      });
    }

    setOpen(!open);
  };

  // ✅ close on outside click
  useEffect(() => {
    const close = () => setOpen(false);
    if (open) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const options = ["ACTIVE", "INACTIVE"];

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        className={`px-4 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1
          ${isActive
            ? "bg-emerald-100 text-emerald-700 border-emerald-300"
            : "bg-red-100 text-red-700 border-red-300"
          }`}
      >
        {status}
        <ChevronDown
          size={14}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        createPortal(
          <div
            style={{ top: position.top, left: position.left }}
            className="fixed z-[9999] w-36 bg-white border border-slate-200 rounded-xl shadow-lg ring-1 ring-slate-200 py-1"
          >
            {options.map((opt) => (
              <button
                key={opt}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(opt);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-slate-50 flex items-center justify-between
                  ${opt === status ? "text-indigo-600" : "text-slate-700"}
                `}
              >
                {opt}
                {opt === status && <Check size={14} />}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

// 2. DESIGN LIST
const DesignList = ({ designs, masterData, onEdit, onCreate, onStatusChange, isLoading }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredDesigns = designs.filter(design => 
    (design.design_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (design.design_code?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const getCategoryName = (id) => masterData.categories.find(c => c.id === id)?.name || 'Unknown';

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 bg-slate-50/50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-indigo-600" size={32} />
          <p>Loading designs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow duration-300 flex flex-col h-full">
      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-800 tracking-tight">Design Masters</h2>
          <p className="text-sm text-slate-500 mt-1">Manage blueprints for production</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative hidden md:block">
              <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder="Search by code or name..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-64 bg-white shadow-sm" 
              />
            </div>
          <button 
            onClick={onCreate}
            className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white px-5 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <Plus size={18} />
            Create New Design
          </button>
        </div>
      </div>
      
      <div className="overflow-auto flex-1">
        <table className="w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50/50 text-slate-700 font-semibold uppercase text-xs tracking-wider sticky top-0 z-10 backdrop-blur-sm">
            <tr>
              <th className="px-6 py-4 border-b border-slate-100">Code</th>
              <th className="px-6 py-4 border-b border-slate-100">Name</th>
              <th className="px-6 py-4 border-b border-slate-100">Category</th>
              <th className="px-6 py-4 text-center border-b border-slate-100">Weight</th>
              <th className="px-6 py-4 text-center border-b border-slate-100">Status</th>
              <th className="px-6 py-4 text-right border-b border-slate-100">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredDesigns.map((design) => (
              <tr 
                key={design.design_id} 
                className="group hover:bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ease-out border-l-4 border-transparent hover:border-indigo-500 relative z-0 hover:z-10"
              >
                <td className="px-6 py-4 font-bold text-indigo-900 group-hover:text-indigo-600 transition-colors">{design.design_code}</td>
                <td className="px-6 py-4 font-medium text-slate-800">
                    <div className="flex items-center gap-3">
                        {design.image && <img src={design.image} className="w-8 h-8 rounded object-cover border border-slate-200" alt="" onError={(e)=>e.target.style.display='none'}/>}
                        {design.design_name}
                    </div>
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 group-hover:bg-indigo-100 group-hover:border-indigo-200 transition-colors">
                    {getCategoryName(design.category_id)}
                  </span>
                </td>
                <td className="px-6 py-4 text-center font-mono text-slate-600">{design.actual_weight || design.weight_3d}g</td>
                <td className="px-6 py-4 text-center">
                  <StatusDropdown
  status={design.status}
  onChange={(newStatus) =>
    onStatusChange(design.design_id, newStatus)
  }
/>
                </td>
                <td className="px-6 py-4 text-right">
                  <button 
                    onClick={() => onEdit(design)}
                    className="text-indigo-600 hover:text-indigo-800 font-bold text-sm flex items-center gap-1 ml-auto group-hover:translate-x-1 transition-transform"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                </td>
              </tr>
            ))}
            {filteredDesigns.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <Search size={32} className="text-slate-300" />
                    <p>No designs found matching your search.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 3. DESIGN CATALOGUE
const DesignCatalogue = ({ designs, masterData, onView, isLoading }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredDesigns = designs.filter(design => 
    (design.design_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (design.design_code?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const getCategoryName = (id) => masterData.categories.find(c => c.id === id)?.name || 'Unknown';
  const getDeptName = (id) => masterData.departments.find(d => d.id === id)?.name || 'Unknown';

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 bg-slate-50/50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-indigo-600" size={32} />
          <p>Loading catalogue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow duration-300 flex flex-col h-full">
      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-800 tracking-tight">Design Catalogue</h2>
          <p className="text-sm text-slate-500 mt-1">Browse all production-ready designs</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative hidden md:block">
              <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder="Search by code or name..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-64 bg-white shadow-sm" 
              />
            </div>
        </div>
      </div>
      <div className="overflow-auto flex-1">
        <table className="w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50/50 text-slate-700 font-semibold uppercase text-xs tracking-wider sticky top-0 z-10 backdrop-blur-sm">
            <tr>
              <th className="px-6 py-4 border-b border-slate-100">Code</th>
              <th className="px-6 py-4 border-b border-slate-100">Name</th>
              <th className="px-6 py-4 border-b border-slate-100">Category</th>
              <th className="px-6 py-4 text-center border-b border-slate-100">Weight</th>
              <th className="px-6 py-4 text-center border-b border-slate-100">Status</th>
              <th className="px-6 py-4 text-right border-b border-slate-100">View</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredDesigns.map((design) => (
              <tr 
                key={design.design_id} 
                className="group hover:bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ease-out border-l-4 border-transparent hover:border-indigo-500 relative z-0 hover:z-10"
              >
                <td className="px-6 py-4 font-bold text-indigo-900 group-hover:text-indigo-600 transition-colors">{design.design_code}</td>
                <td className="px-6 py-4 font-medium text-slate-800">
                    <div className="flex items-center gap-3">
                        {design.image && <img src={design.image} className="w-8 h-8 rounded object-cover border border-slate-200" alt="" onError={(e)=>e.target.style.display='none'}/>}
                        {design.design_name}
                    </div>
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 group-hover:bg-indigo-100 group-hover:border-indigo-200 transition-colors">
                    {getCategoryName(design.category_id)}
                  </span>
                </td>
                <td className="px-6 py-4 text-center font-mono text-slate-600">{design.actual_weight || design.weight_3d}g</td>
                <td className="px-6 py-4 text-center">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold shadow-sm transition-all ${{
                    ACTIVE: 'bg-emerald-100 text-emerald-700',
                    INACTIVE: 'bg-slate-100 text-slate-500',
                  }[design.status] || 'bg-amber-100 text-amber-700'}`}>
                    {design.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button 
                    onClick={() => onView(design)}
                    className="text-indigo-600 hover:text-indigo-800 font-bold text-sm flex items-center gap-1 ml-auto group-hover:translate-x-1 transition-transform"
                  >
                    <ChevronRight size={16} />
                    View
                  </button>
                </td>
              </tr>
            ))}
            {filteredDesigns.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <Search size={32} className="text-slate-300" />
                    <p>No designs found matching your search.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 4. STAGE ITEM COMPONENT
const StageItem = ({ stage, index, total, onMove, onToggle, onUpdate, onDragStart, onDragOver, onDrop, type, availableMaterials = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // State for new material entry
  const [newMat, setNewMat] = useState({ material_name: '', material_id: null, qty: '', unit: 'g' });
  const [isCustomMaterial, setIsCustomMaterial] = useState(false);

  // Load existing materials from the stage object
  const materials = stage.default_materials || [];

  // Handle Selection from Dropdown
  const handleMaterialSelect = (e) => {
    const value = e.target.value;
    
    if (value === 'custom') {
      setIsCustomMaterial(true);
      setNewMat({ material_name: '', material_id: null, qty: '', unit: 'g' });
    } else {
      setIsCustomMaterial(false);
      const selectedMat = availableMaterials.find(m => m.material_id.toString() === value);
      if (selectedMat) {
        setNewMat({
          material_name: selectedMat.material_name,
          material_id: selectedMat.material_id,
          qty: newMat.qty, // Keep qty if user entered it first
          unit: selectedMat.unit || 'g' // Use unit from API
        });
      }
    }
  };

  const handleAddMaterial = () => {
    if (!newMat.material_name) return;
    const updatedMaterials = [...materials, newMat];
    onUpdate({ default_materials: updatedMaterials });
    
    // Reset inputs
    setNewMat({ material_name: '', material_id: null, qty: '', unit: 'g' });
    setIsCustomMaterial(false); // Reset to dropdown mode
  };

  const handleRemoveMaterial = (mIndex) => {
    const updatedMaterials = materials.filter((_, i) => i !== mIndex);
    onUpdate({ default_materials: updatedMaterials });
  };

  return (
    <div 
      className={`border rounded-lg mb-2 transition-all group ${
        stage.enabled !== false 
          ? 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-sm' 
          : 'bg-slate-50 border-slate-100 opacity-60'
      }`}
      draggable={stage.enabled !== false}
      onDragStart={(e) => onDragStart(e, type, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, type, index)}
    >
      <div className="flex items-center gap-3 p-3">
        <div className={`flex-shrink-0 cursor-grab active:cursor-grabbing text-slate-300 hover:text-indigo-500 transition-colors ${stage.enabled === false && 'pointer-events-none'}`}>
          <GripVertical size={18} />
        </div>
        
        {/* Checkbox for Selection */}
        <button 
          onClick={() => onToggle()}
          className={`flex-shrink-0 transition-colors ${
            stage.enabled !== false ? 'text-indigo-600' : 'text-slate-300 hover:text-slate-400'
          }`}
        >
          {stage.enabled !== false ? <CheckSquare size={20} /> : <Square size={20} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className={`font-medium truncate ${stage.enabled !== false ? 'text-slate-800' : 'text-slate-500 line-through'}`}>
            {stage.label || stage.stage_name}
          </div>
          <div className="flex items-center gap-2">
             <div className="text-xs text-slate-500 font-mono">{stage.stage_code}</div>
             {materials.length > 0 && stage.enabled !== false && (
                <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                   <Layers size={10}/> {materials.length} Materials
                </span>
             )}
          </div>
        </div>

        <div className="flex items-center flex-shrink-0 gap-2">
          {/* Static QC Indicator */}
          {stage.enabled !== false ? (
             <div 
               className={`w-6 h-6 flex items-center justify-center rounded border text-[10px] font-bold shadow-sm select-none ${
                 stage.qc_required 
                   ? 'bg-emerald-100 border-emerald-200 text-emerald-700' 
                   : 'bg-rose-50 border-rose-200 text-rose-400'
               }`}
               title={stage.qc_required ? "QC Required" : "No QC Required"}
             >
               QC
             </div>
          ) : (
             <span className="text-[10px] text-slate-400 font-medium px-1">Skip</span>
          )}

          {/* Expand/Collapse Button */}
          {stage.enabled !== false && (
             <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className={`p-1 rounded hover:bg-slate-100 transition-colors ${isExpanded ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400'}`}
             >
                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
             </button>
          )}
        </div>
      </div>

      {/* EXPANDABLE MATERIAL SECTION */}
      {isExpanded && stage.enabled !== false && (
         <div className="px-4 pb-4 pt-0 animate-in slide-in-from-top-2 duration-200">
            <div className="pt-3 border-t border-slate-100">
               <h4 className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1">
                  <Layers size={12}/> Stage Materials
               </h4>
               
               {/* List Existing Materials */}
               <div className="space-y-2 mb-3">
                  {materials.map((mat, idx) => (
                     <div key={idx} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded border border-slate-100">
                        <div className="flex flex-col">
                           <span className="font-medium text-slate-700">{mat.material_name}</span>
                           {mat.material_id ? (
                              <span className="text-[10px] text-indigo-500 flex items-center gap-1">
                                 <CheckCircle2 size={10}/> Sys ID: {mat.material_id}
                              </span>
                           ) : (
                              <span className="text-[10px] text-slate-400 italic flex items-center gap-1">
                                 <Beaker size={10}/> Custom
                              </span>
                           )}
                        </div>
                        <div className="flex items-center gap-3">
                           <span className="text-slate-500 font-mono">{mat.qty} {mat.unit}</span>
                           <button onClick={() => handleRemoveMaterial(idx)} className="text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={14}/></button>
                        </div>
                     </div>
                  ))}
                  {materials.length === 0 && <div className="text-xs text-slate-400 italic">No specific materials defined for this stage.</div>}
               </div>

               {/* Add New Material Inputs */}
               <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                     {/* Material Selection / Entry */}
                     <div className="flex-1">
                        {!isCustomMaterial ? (
                           <select 
                              className="w-full p-1.5 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white"
                              value={newMat.material_id || ''}
                              onChange={handleMaterialSelect}
                           >
                              <option value="">Select Material...</option>
                              {availableMaterials.map(mat => (
                                 <option key={mat.material_id} value={mat.material_id}>
                                    {mat.material_name} ({mat.material_code})
                                 </option>
                              ))}
                              <option disabled>──────────</option>
                              <option value="custom" className="font-bold text-indigo-600">+ Custom / New Material</option>
                           </select>
                        ) : (
                           <div className="flex gap-1">
                              <input 
                                 type="text" 
                                 placeholder="Enter Material Name" 
                                 autoFocus
                                 className="flex-1 p-1.5 text-xs border border-indigo-300 rounded outline-none focus:border-indigo-500 bg-indigo-50/20"
                                 value={newMat.material_name}
                                 onChange={(e) => setNewMat({...newMat, material_name: e.target.value})}
                              />
                              <button 
                                 onClick={() => { setIsCustomMaterial(false); setNewMat({...newMat, material_name: '', material_id: null}); }}
                                 className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded"
                                 title="Back to List"
                              >
                                 <X size={12}/>
                              </button>
                           </div>
                        )}
                     </div>

                     {/* Quantity Input */}
                     <input 
                        type="number" 
                        placeholder="Qty" 
                        className="w-16 p-1.5 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500"
                        value={newMat.qty}
                        onChange={(e) => setNewMat({...newMat, qty: e.target.value})}
                     />

                     {/* Unit Selection */}
                     <select 
                        className="w-16 p-1.5 text-xs border border-slate-300 rounded outline-none bg-white"
                        value={newMat.unit}
                        onChange={(e) => setNewMat({...newMat, unit: e.target.value})}
                        disabled={!isCustomMaterial && newMat.material_id} // Disable unit edit if from API (optional rule, enabled here for flexibility)
                     >
                        <option value="g">g</option>
                        <option value="kg">kg</option>
                        <option value="ml">ml</option>
                        <option value="pcs">pcs</option>
                     </select>

                     {/* Add Button */}
                     <button 
                        onClick={handleAddMaterial}
                        disabled={!newMat.material_name || !newMat.qty}
                        className="p-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-slate-300 transition-colors"
                     >
                        <Plus size={14}/>
                     </button>
                  </div>
               </div>
            </div>
         </div>
      )}
    </div>
  );
};

// 5. DESIGN FORM
const DesignForm = ({ initialData, masterData, materialsList, onSave, onCancel, isLoading = false }) => {
  const CUSTOM_FAMILY_VALUE = '__custom_family__';
  const { getOptions, getValues } = useMasterData(['STONE_TYPE', 'DESIGN_TARGET_COST_UNIT', 'DESIGN_TYPICAL_PRICE_UNIT']);
  const stoneTypeMasterOptions = getOptions('STONE_TYPE');
  const stoneTypeValues = getValues('STONE_TYPE');
  const targetCostUnitOptions = getOptions('DESIGN_TARGET_COST_UNIT');
  const typicalPriceUnitOptions = getOptions('DESIGN_TYPICAL_PRICE_UNIT');
  // Form State
  const [formData, setFormData] = useState(initialData ? {
    ...initialData,
    dimensions: {
      length: initialData.dimensions?.length || '',
      width: initialData.dimensions?.width || '',
      height: initialData.dimensions?.height || '',
      target_cost_unit: initialData.dimensions?.target_cost_unit || 'PCS',
      typical_price_unit: initialData.dimensions?.typical_price_unit || 'PCS',
      parts: normalizeDesignParts(initialData.dimensions?.parts || [])
    },
    stones: (initialData.stones || []).map(normalizeDesignStone),
    stages: sanitizeStageGroups(initialData.stages || {})
  } : {
    department_id: '',
    design_code: '',
    design_name: '',
    category_id: '',
    customer_id: '',
    family: '',
    actual_weight: '',
    weight_3d: '',
    target_cost_per_piece: '',
    typical_price_min: '',
    typical_price_max: '',
    image: '',
    description: '',
    dimensions: { length: '', width: '', height: '', target_cost_unit: 'PCS', typical_price_unit: 'PCS', parts: [createDefaultPart(0)] },
    stones: [],
    category_sizes: { US: [], IN: [] },
    stages: {
      pre_casting: [...DEFAULT_PRE_CASTING],
      casting: [...DEFAULT_CASTING],
      post_casting: [...DEFAULT_POST_CASTING]
    }
  });

  // Keep workflow defaults aligned when editing an existing design.
  useEffect(() => {
    if (initialData && initialData.stages) {
        setFormData(prev => ({
            ...prev,
            stages: sanitizeStageGroups(initialData.stages)
        }));
    }
  }, [initialData]);

  const [activeTab, setActiveTab] = useState('basic'); // basic, specs, workflow, image
  const [isDragging, setIsDragging] = useState(false); // For image upload
  const [draggedStage, setDraggedStage] = useState(null); // For workflow dnd
  const [customFamilyName, setCustomFamilyName] = useState('');
  const formTabs = [
    { id: 'basic', label: '1. Basic Details', shortLabel: 'Basic Details', icon: FileText },
    { id: 'specs', label: '2. Specifications', shortLabel: 'Specifications', icon: Gem },
    { id: 'workflow', label: '3. Workflow & Stages', shortLabel: 'Workflow & Stages', icon: Settings },
    { id: 'image', label: '4. Image', shortLabel: 'Image', icon: ImageIcon },
  ];
  const activeTabIndex = formTabs.findIndex((tab) => tab.id === activeTab);
  const nextTab = activeTabIndex >= 0 ? formTabs[activeTabIndex + 1] : null;
  const familyOptions = useMemo(() => {
    const seen = new Set();
    return (masterData.families || [])
      .filter((family) => family?.id && family?.name)
      .filter((family) => {
        if (seen.has(family.id)) return false;
        seen.add(family.id);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [masterData.families]);
  const isCustomFamilySelected = formData.family_id === CUSTOM_FAMILY_VALUE;
    
  // -- New Stage State --
  const [addingStageType, setAddingStageType] = useState(null);
  const [newStageName, setNewStageName] = useState('');
  const [newStageQC, setNewStageQC] = useState(false);
  const [isStonePickerOpen, setIsStonePickerOpen] = useState(false);
  const [stoneSearch, setStoneSearch] = useState('');
  const [stoneTypeFilter, setStoneTypeFilter] = useState(DEFAULT_STONE_TYPE_FILTER);
  const [stoneSizeFilter, setStoneSizeFilter] = useState('');
  const [stoneShapeFilter, setStoneShapeFilter] = useState('');

  useEffect(() => {
    if (stoneTypeValues.length && !stoneTypeValues.includes(stoneTypeFilter)) {
      setStoneTypeFilter(stoneTypeValues[0]);
    }
  }, [stoneTypeFilter, stoneTypeValues]);

  // --- Handlers ---

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'family_id') {
      setFormData(prev => ({ ...prev, family_id: value }));
      if (value !== CUSTOM_FAMILY_VALUE) {
        setCustomFamilyName('');
      }
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      dimensions: { ...prev.dimensions, [name]: value }
    }));
  };

  const addPart = () => {
    setFormData((prev) => {
      const nextParts = [...(prev.dimensions.parts || []), createDefaultPart(prev.dimensions.parts?.length || 0)];
      return {
        ...prev,
        dimensions: {
          ...prev.dimensions,
          parts: nextParts,
        },
      };
    });
  };

  const removePart = (partIndex) => {
    setFormData((prev) => {
      const existingParts = prev.dimensions.parts || [];
      const nextParts = existingParts.filter((_, index) => index !== partIndex);
      return {
        ...prev,
        dimensions: {
          ...prev.dimensions,
          parts: nextParts.length > 0 ? nextParts : [createDefaultPart(0)],
        },
      };
    });
  };

  const updatePart = (partIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        parts: (prev.dimensions.parts || []).map((part, index) => (
          index === partIndex ? { ...part, [field]: value } : part
        )),
      },
    }));
  };

  const addPartMaterial = (partIndex) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        parts: (prev.dimensions.parts || []).map((part, index) => (
          index === partIndex
            ? { ...part, materials: [...(part.materials || []), createDefaultPartMaterial()] }
            : part
        )),
      },
    }));
  };

  const removePartMaterial = (partIndex, materialIndex) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        parts: (prev.dimensions.parts || []).map((part, index) => {
          if (index !== partIndex) return part;
          const nextMaterials = (part.materials || []).filter((_, idx) => idx !== materialIndex);
          return {
            ...part,
            materials: nextMaterials.length > 0 ? nextMaterials : [createDefaultPartMaterial()],
          };
        }),
      },
    }));
  };

  const updatePartMaterial = (partIndex, materialIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        parts: (prev.dimensions.parts || []).map((part, index) => {
          if (index !== partIndex) return part;
          const nextMaterials = (part.materials || []).map((material, idx) => {
            if (idx !== materialIndex) return material;
            if (field === 'material_id') {
              const selectedMaterial = materialsList.find((item) => String(item.material_id || item.id) === String(value));
              return {
                ...material,
                material_id: value,
                material_name: selectedMaterial?.material_name || selectedMaterial?.name || '',
                unit: selectedMaterial?.unit || '',
              };
            }
            return { ...material, [field]: value };
          });
          return { ...part, materials: nextMaterials };
        }),
      },
    }));
  };

  const addStone = () => {
    setStoneSearch('');
    setStoneTypeFilter(stoneTypeValues[0] || DEFAULT_STONE_TYPE_FILTER);
    setStoneSizeFilter('');
    setStoneShapeFilter('');
    setIsStonePickerOpen(true);
  };

  const handleSelectStone = (stoneOption) => {
    const selectedStone = buildStonePayloadFromInventory(stoneOption);
    setFormData((prev) => {
      const existingIndex = (prev.stones || []).findIndex((stone) => (
        String(stone.material_id || '') === String(selectedStone.material_id || '')
      ));
      if (existingIndex >= 0) {
        const nextStones = [...prev.stones];
        nextStones[existingIndex] = {
          ...nextStones[existingIndex],
          ...selectedStone,
          count: Number(nextStones[existingIndex].count || 0) + 1,
        };
        return { ...prev, stones: nextStones };
      }
      return {
        ...prev,
        stones: [...(prev.stones || []), selectedStone],
      };
    });
    setIsStonePickerOpen(false);
    setStoneSearch('');
  };

  const updateStone = (index, field, value) => {
    const newStones = [...formData.stones];
    newStones[index][field] = value;
    setFormData(prev => ({ ...prev, stones: newStones }));
  };

  const removeStone = (index) => {
    setFormData(prev => ({
      ...prev,
      stones: prev.stones.filter((_, i) => i !== index)
    }));
  };

  // --- STAGE MANIPULATION HANDLERS ---

  const moveStage = (type, index, direction) => {
    const listKey = type === 'pre' ? 'pre_casting' : type === 'casting' ? 'casting' : 'post_casting';
    const newList = [...formData.stages[listKey]];
    const [movedItem] = newList.splice(index, 1);
    newList.splice(index + direction, 0, movedItem);
    
    // Re-assign sequence numbers
    const sequencedList = newList.map((item, idx) => ({ ...item, sequence: idx + 1 }));

    setFormData(prev => ({
      ...prev,
      stages: { ...prev.stages, [listKey]: sequencedList }
    }));
  };

  const reorderStage = (listKey, fromIndex, toIndex) => {
    const newList = [...formData.stages[listKey]];
    const [movedItem] = newList.splice(fromIndex, 1);
    newList.splice(toIndex, 0, movedItem);
    
    const sequencedList = newList.map((item, idx) => ({ ...item, sequence: idx + 1 }));

    setFormData(prev => ({
      ...prev,
      stages: { ...prev.stages, [listKey]: sequencedList }
    }));
  };

  const toggleStage = (type, index) => {
    const listKey = type === 'pre' ? 'pre_casting' : type === 'casting' ? 'casting' : 'post_casting';
    const newList = [...formData.stages[listKey]];
    newList[index] = { ...newList[index], enabled: newList[index].enabled === false ? true : false };
    setFormData(prev => ({
      ...prev,
      stages: { ...prev.stages, [listKey]: newList }
    }));
  };

  const updateStage = (type, index, newData) => {
    const listKey = type === 'pre' ? 'pre_casting' : type === 'casting' ? 'casting' : 'post_casting';
    const newList = [...formData.stages[listKey]];
    // Merge new data into the specific stage
    newList[index] = { ...newList[index], ...newData };
    
    setFormData(prev => ({
        ...prev,
        stages: { ...prev.stages, [listKey]: newList }
    }));
  };

  const startAddingStage = (type) => {
    setAddingStageType(type);
    setNewStageName('');
    setNewStageQC(false);
  };

  const cancelAddingStage = () => {
    setAddingStageType(null);
  };

  const saveNewStage = () => {
    if (!newStageName.trim()) return;
    
    const listKey = addingStageType === 'pre' ? 'pre_casting' : addingStageType === 'casting' ? 'casting' : 'post_casting';
    const currentList = formData.stages[listKey];
    
    const newStage = {
      stage_code: newStageName.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
      label: newStageName,
      qc_required: newStageQC,
      sequence: currentList.length + 1,
      enabled: true,
      default_materials: []
    };

    setFormData(prev => ({
      ...prev,
      stages: {
        ...prev.stages,
        [listKey]: [newStage, ...currentList].map((item, idx) => ({ ...item, sequence: idx + 1 }))
      }
    }));
    
    setAddingStageType(null);
  };

  // -- Workflow DND --
  const handleStageDragStart = (e, type, index) => {
    setDraggedStage({ type, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleStageDragOver = (e) => {
    e.preventDefault(); 
  };

  const handleStageDrop = (e, type, index) => {
    e.preventDefault();
    if (!draggedStage) return;
    if (draggedStage.type !== type) return; // Restrict to same column
    if (draggedStage.index === index) return;

    const listKey = type === 'pre' ? 'pre_casting' : type === 'casting' ? 'casting' : 'post_casting';
    reorderStage(listKey, draggedStage.index, index);
    setDraggedStage(null);
  };

  // -- Image Upload DND --
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isCustomFamilySelected && !customFamilyName.trim()) {
      alert('Please enter a family / collection name.');
      return;
    }
    onSave({
      ...formData,
      family_id: isCustomFamilySelected ? '' : formData.family_id,
      family_name: isCustomFamilySelected ? customFamilyName.trim() : '',
      stones: (formData.stones || []).map(normalizeDesignStone),
      stages: sanitizeStageGroups(formData.stages)
    });
  };

  const stoneSizeOptions = useMemo(() => (
    Array.from(new Set(
      (masterData.stones || [])
        .map((stone) => String(stone.size || '').trim())
        .filter(Boolean)
    )).sort((a, b) => a.localeCompare(b))
  ), [masterData.stones]);

  const stoneShapeOptions = useMemo(() => (
    Array.from(new Set(
      (masterData.stones || [])
        .map((stone) => String(stone.shape || '').trim())
        .filter(Boolean)
    )).sort((a, b) => a.localeCompare(b))
  ), [masterData.stones]);

  const filteredStoneOptions = (masterData.stones || []).filter((stone) => {
    const normalizedStoneType = normalizeStoneFilterValue(stone.stone_type || stone.material_type);
    const normalizedFilterType = normalizeStoneFilterValue(stoneTypeFilter);
    if (normalizedFilterType && normalizedStoneType !== normalizedFilterType) {
      return false;
    }
    if (stoneSizeFilter && String(stone.size || '').trim() !== stoneSizeFilter) {
      return false;
    }
    if (stoneShapeFilter && String(stone.shape || '').trim() !== stoneShapeFilter) {
      return false;
    }
    if (!stoneSearch.trim()) return true;
    const query = stoneSearch.toLowerCase();
    const haystack = [
      stone.name,
      stone.material_type,
      stone.stone_type,
      stone.quality_grade,
      stone.clarity,
      stone.cut,
      stone.color,
      stone.size,
      stone.shape,
      stone.material_id,
    ].join(' ').toLowerCase();
    return haystack.includes(query);
  });

  // Helper to render the add stage form or button
  const renderAddStageSection = (type) => {
    if (addingStageType === type) {
      return (
        <div className="p-3 bg-white border-2 border-indigo-100 rounded-lg mb-2 shadow-sm animate-in fade-in zoom-in-95 duration-200">
          <input 
             autoFocus
             type="text" 
             placeholder="Stage Name"
             className="w-full p-2 text-sm border border-slate-300 rounded focus:border-indigo-500 outline-none mb-3" 
             value={newStageName} 
             onChange={(e) => setNewStageName(e.target.value)}
             onKeyDown={(e) => e.key === 'Enter' && saveNewStage()}
          />
          <div className="flex justify-between items-center">
             <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer select-none">
                <div className={`w-4 h-4 border rounded flex items-center justify-center transition-colors ${newStageQC ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-300'}`}>
                   {newStageQC && <Check size={10} />}
                </div>
                <input type="checkbox" checked={newStageQC} onChange={(e) => setNewStageQC(e.target.checked)} className="hidden" />
                QC Required
             </label>
             <div className="flex gap-2">
                <button onClick={cancelAddingStage} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"><X size={16}/></button>
                <button onClick={saveNewStage} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check size={16}/></button>
             </div>
          </div>
        </div>
      );
    }
    
    return (
      <button
        onClick={() => startAddingStage(type)}
        className="w-full py-3 mb-2 flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg text-slate-400 text-xs font-bold uppercase tracking-wider hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
      >
        <Plus size={16} />
        Add New Stage
      </button>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-xl border border-slate-200 flex flex-col h-[calc(100vh-100px)] overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-white sticky top-0 z-20">
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            {initialData ? 'Edit Design Master' : 'Create Design Master'}
          </h2>
          <p className="text-sm text-slate-500">Define specs and production workflow</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors border border-transparent hover:border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {isLoading ? 'Saving Master...' : 'Save Master'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-white sticky top-[72px] z-10 px-6 pt-2">
        {formTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 pt-2 px-6 flex items-center justify-center gap-2 text-sm font-medium border-b-2 transition-all ${
              activeTab === tab.id 
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-lg' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-t-lg'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-8 bg-slate-50/50">
        <div className="max-w-7xl mx-auto space-y-8 pb-10">
          
          {/* SECTION A: BASIC DETAILS */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="col-span-2 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100 flex items-start gap-3 shadow-sm hover:shadow-md transition-shadow duration-300">
                 <AlertCircle className="text-blue-600 shrink-0 mt-0.5" size={18} />
                 <p className="text-sm text-blue-800 font-medium">Design Code must be unique across the organization. Once used in production, basic details cannot be changed easily.</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Design Code <span className="text-red-500">*</span></label>
                <input 
                  type="text" name="design_code" 
                  value={formData.design_code} onChange={handleChange}
                  placeholder="e.g. RNG-1023"
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow shadow-sm"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Design Name <span className="text-red-500">*</span></label>
                <input 
                  type="text" name="design_name" 
                  value={formData.design_name} onChange={handleChange}
                  placeholder="e.g. Diamond Ring Classic"
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow shadow-sm"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
  Department <span className="text-slate-400 text-xs">(Optional)</span>
</label>
                <select 
                  name="department_id" 
                  value={formData.department_id} onChange={handleChange}
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white shadow-sm"
                >
                  <option value="">Select Department</option>
                  {masterData.departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Category <span className="text-red-500">*</span></label>
                <select 
                  name="category_id" 
                  value={formData.category_id} onChange={handleChange}
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white shadow-sm"
                >
                  <option value="">Select Category</option>
                  {masterData.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Customer (Optional)</label>
                <select 
                  name="customer_id" 
                  value={formData.customer_id} onChange={handleChange}
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white shadow-sm"
                >
                  <option value="">General Stock</option>
                  {masterData.customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Family/Collection</label>
                <select 
                  name="family_id" 
                  value={formData.family_id} onChange={handleChange}
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white shadow-sm"
                >
                  <option value="">Select Family</option>
                  {familyOptions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  <option value={CUSTOM_FAMILY_VALUE}>+ Add New Family</option>
                </select>
                {isCustomFamilySelected && (
                  <input
                    type="text"
                    value={customFamilyName}
                    onChange={(e) => setCustomFamilyName(e.target.value)}
                    placeholder="Enter new family / collection name"
                    className="mt-3 w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white shadow-sm"
                  />
                )}
              </div>
            </div>
          )}

          {/* SECTION B: SPECIFICATIONS */}
          {activeTab === 'specs' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
              
              {/* Weights & Dimensions */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all duration-300">
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600"><Package size={18}/></div> 
                  Physical Properties
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Actual Wt (g)</label>
                      <input type="number" name="actual_weight" step="0.01" value={formData.actual_weight} onChange={handleChange} className="w-full p-2.5 border border-slate-300 rounded-lg text-right font-mono focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0.00" />
                  </div>
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">3D Wt (g)</label>
                      <input type="number" name="weight_3d" step="0.01" value={formData.weight_3d} onChange={handleChange} className="w-full p-2.5 border border-slate-300 rounded-lg text-right font-mono focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0.00" />
                  </div>
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Target Cost</label>
                      <input type="number" name="target_cost_per_piece" step="0.01" value={formData.target_cost_per_piece} onChange={handleChange} className="w-full p-2.5 border border-slate-300 rounded-lg text-right font-mono focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0.00" />
                  </div>
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Target Cost Unit</label>
                      <select name="target_cost_unit" value={formData.dimensions.target_cost_unit || 'PCS'} onChange={handleDimensionChange} className="w-full p-2.5 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none">
                        {targetCostUnitOptions.map((option) => <option key={option.value} value={option.value}>{option.label || option.value}</option>)}
                      </select>
                  </div>
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Typical Price Min (Rs.)</label>
                      <input type="number" name="typical_price_min" step="0.01" value={formData.typical_price_min} onChange={handleChange} className="w-full p-2.5 border border-slate-300 rounded-lg text-right font-mono focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0.00" />
                  </div>
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Typical Price Max (Rs.)</label>
                      <input type="number" name="typical_price_max" step="0.01" value={formData.typical_price_max} onChange={handleChange} className="w-full p-2.5 border border-slate-300 rounded-lg text-right font-mono focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0.00" />
                  </div>
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Typical Price Unit</label>
                      <select name="typical_price_unit" value={formData.dimensions.typical_price_unit || 'PCS'} onChange={handleDimensionChange} className="w-full p-2.5 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none">
                        {typicalPriceUnitOptions.map((option) => <option key={option.value} value={option.value}>{option.label || option.value}</option>)}
                      </select>
                  </div>
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Length (mm)</label>
                      <input type="text" name="length" value={formData.dimensions.length} onChange={handleDimensionChange} className="w-full p-2.5 border border-slate-300 rounded-lg text-right font-mono focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0" />
                  </div>
                  <div className="space-y-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Width (mm)</label>
                      <input type="text" name="width" value={formData.dimensions.width} onChange={handleDimensionChange} className="w-full p-2.5 border border-slate-300 rounded-lg text-right font-mono focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="0" />
                  </div>
                </div>

                <div className="mt-8 border-t border-slate-200 pt-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-slate-800">Parts & Materials</h4>
                      <p className="text-sm text-slate-500">Define design parts like links, hooks, pendants, and the materials needed for each part.</p>
                    </div>
                    <button
                      type="button"
                      onClick={addPart}
                      className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                    >
                      <Plus size={14} />
                      Add Part
                    </button>
                  </div>

                  <div className="space-y-4">
                    {(formData.dimensions.parts || []).map((part, partIndex) => (
                      <div key={part.id || `part-${partIndex}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-4 flex items-center justify-between gap-3">
                          <div className="flex-1">
                            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Part Name</label>
                            <input
                              type="text"
                              value={part.name}
                              onChange={(e) => updatePart(partIndex, 'name', e.target.value)}
                              placeholder={`Part ${partIndex + 1}`}
                              className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removePart(partIndex)}
                            className="mt-5 rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                            title="Remove part"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="space-y-3">
                          {(part.materials || []).map((material, materialIndex) => (
                            <div key={`${part.id || partIndex}-material-${materialIndex}`} className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-3 md:grid-cols-[minmax(0,1.8fr)_120px_110px_auto] md:items-end">
                              <div>
                                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Material</label>
                                <select
                                  value={material.material_id}
                                  onChange={(e) => updatePartMaterial(partIndex, materialIndex, 'material_id', e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                >
                                  <option value="">Select Material</option>
                                  {materialsList.map((item) => (
                                    <option key={item.material_id || item.id} value={item.material_id || item.id}>
                                      {item.material_name || item.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Qty Needed</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={material.qty}
                                  onChange={(e) => updatePartMaterial(partIndex, materialIndex, 'qty', e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                  placeholder="0.00"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Unit</label>
                                <input
                                  type="text"
                                  value={material.unit}
                                  onChange={(e) => updatePartMaterial(partIndex, materialIndex, 'unit', e.target.value)}
                                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                  placeholder="g / pcs"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removePartMaterial(partIndex, materialIndex)}
                                className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                                title="Remove material"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          onClick={() => addPartMaterial(partIndex)}
                          className="mt-4 inline-flex items-center gap-2 rounded-lg border border-dashed border-indigo-300 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                        >
                          <Plus size={14} />
                          Add Material To {part.name || `Part ${partIndex + 1}`}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stone Details */}
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all duration-300">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600"><Gem size={18}/></div>
                    Stone Details
                  </h3>
                  <button type="button" onClick={addStone} className="text-xs flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-3 py-2 rounded-lg transition-colors border border-indigo-200 font-medium">
                    <Plus size={14} /> Add Stone
                  </button>
                </div>
                
                <div className="space-y-3">
                  {formData.stones.map((stone, index) => (
                    <div key={`${stone.material_id || stone.name || 'stone'}-${index}`} className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-indigo-300 transition-colors space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Stone</div>
                          <div className="mt-1 font-semibold text-slate-800">{stone.name || stone.type || '-'}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Type</div>
                          <div className="mt-1 font-semibold text-slate-800">{stone.material_type || stone.type || '-'}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Quality / Clarity</div>
                          <div className="mt-1 font-semibold text-slate-800">{[stone.quality_grade || 'Standard', stone.clarity || 'N/A'].filter(Boolean).join(' / ')}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Cut / Color</div>
                          <div className="mt-1 font-semibold text-slate-800">{[stone.cut, stone.color].filter(Boolean).join(' / ') || '-'}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Size</div>
                          <div className="mt-1 font-semibold text-slate-800">{stone.size || 'N/A'}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-[1fr_160px_140px_140px_auto] gap-4 items-end">
                      <div className="flex-1 space-y-1">
                        <label className="text-xs font-semibold text-slate-500">Selected Stone</label>
                        <div className="w-full p-2 text-sm border border-slate-300 rounded-md bg-white text-slate-700">
                          {stone.name || stone.type || 'Stone'}
                        </div>
                      </div>
                      <div className="w-40 space-y-1">
                        <label className="text-xs font-semibold text-slate-500">Stone Type</label>
                        <select
                          value={stone.stone_type || stone.material_type || ''}
                          onChange={(e) => {
                            updateStone(index, 'stone_type', e.target.value);
                            updateStone(index, 'material_type', e.target.value);
                          }}
                          className="w-full p-2 text-sm border border-slate-300 rounded-md bg-white focus:border-indigo-500 outline-none"
                        >
                          <option value="">Select type</option>
                          {stoneTypeMasterOptions.map((option) => (
                            <option key={`${stone.material_id || index}-${option.value}`} value={option.value}>
                              {option.label || option.value}
                            </option>
                          ))}
                        </select>
                      </div>
                        <div className="w-24 space-y-1">
                        <label className="text-xs font-semibold text-slate-500">Size</label>
                        <input 
                          type="text" value={stone.size} onChange={(e) => updateStone(index, 'size', e.target.value)}
                          className="w-full p-2 text-sm border border-slate-300 rounded-md focus:border-indigo-500 outline-none" 
                          placeholder="3mm / 0.5ct"
                        />
                      </div>
                      <div className="w-24 space-y-1">
                        <label className="text-xs font-semibold text-slate-500">Qty</label>
                        <input 
                          type="number" min="1" value={stone.count} onChange={(e) => updateStone(index, 'count', Math.max(parseInt(e.target.value, 10) || 1, 1))}
                          className="w-full p-2 text-sm border border-slate-300 rounded-md focus:border-indigo-500 outline-none"
                        />
                      </div>
                      <button type="button" onClick={() => removeStone(index)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 size={18} />
                      </button>
                      </div>
                    </div>
                  ))}
                  {formData.stones.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-lg bg-slate-50/50">
                      No stones added to this design.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* SECTION C: WORKFLOW */}
          {activeTab === 'workflow' && (
            <div className="grid grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300 h-full">
              
              {/* Pre-Casting Column */}
              <div className="flex flex-col h-full bg-slate-50/50 rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-4 bg-white border-b border-slate-100 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 border border-orange-100 flex items-center justify-center font-bold text-sm shadow-sm">
                      A
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-sm">Pre-Casting</h4>
                      <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Wax & Tree</p>
                    </div>
                  </div>
                  <div className="px-2 py-1 bg-slate-100 rounded text-[10px] font-bold text-slate-500">
                    {formData.stages.pre_casting.filter(s => s.enabled !== false).length} Steps
                  </div>
                </div>
                
                <div className="flex-1 p-3 overflow-y-auto max-h-[500px] scrollbar-thin scrollbar-thumb-slate-200 space-y-2">
                  {renderAddStageSection('pre')}
                  {formData.stages.pre_casting.map((stage, idx) => (
                    <StageItem 
                      key={stage.stage_code} 
                      stage={stage} 
                      index={idx} 
                      total={formData.stages.pre_casting.length} 
                      onMove={(i, dir) => moveStage('pre', i, dir)} 
                      onToggle={() => toggleStage('pre', idx)}
                      onUpdate={(data) => updateStage('pre', idx, data)}
                      onDragStart={handleStageDragStart}
                      onDragOver={handleStageDragOver}
                      onDrop={handleStageDrop}
                      type="pre"
                      availableMaterials={materialsList} // PASS MATERIALS LIST
                    />
                  ))}
                </div>
              </div>

              {/* Casting Column */}
              <div className="flex flex-col h-full bg-slate-50/50 rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-4 bg-white border-b border-slate-100 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 border border-purple-100 flex items-center justify-center font-bold text-sm shadow-sm">
                      B
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-sm">Casting</h4>
                      <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Molding & Metal</p>
                    </div>
                  </div>
                  <div className="px-2 py-1 bg-slate-100 rounded text-[10px] font-bold text-slate-500">
                    {formData.stages.casting.filter(s => s.enabled !== false).length} Steps
                  </div>
                </div>
                
                <div className="flex-1 p-3 overflow-y-auto max-h-[500px] scrollbar-thin scrollbar-thumb-slate-200 space-y-2">
                  {renderAddStageSection('casting')}
                  {formData.stages.casting.map((stage, idx) => (
                    <StageItem 
                      key={stage.stage_code} 
                      stage={stage} 
                      index={idx} 
                      total={formData.stages.casting.length} 
                      onMove={(i, dir) => moveStage('casting', i, dir)} 
                      onToggle={() => toggleStage('casting', idx)}
                      onUpdate={(data) => updateStage('casting', idx, data)}
                      onDragStart={handleStageDragStart}
                      onDragOver={handleStageDragOver}
                      onDrop={handleStageDrop}
                      type="casting"
                      availableMaterials={materialsList} // PASS MATERIALS LIST
                    />
                  ))}
                </div>
              </div>

              {/* Post-Casting Column */}
              <div className="flex flex-col h-full bg-slate-50/50 rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-4 bg-white border-b border-slate-100 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center font-bold text-sm shadow-sm">
                      C
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-sm">Post-Casting</h4>
                      <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Finishing & QC</p>
                    </div>
                  </div>
                  <div className="px-2 py-1 bg-slate-100 rounded text-[10px] font-bold text-slate-500">
                    {formData.stages.post_casting.filter(s => s.enabled !== false).length} Steps
                  </div>
                </div>

                <div className="flex-1 p-3 overflow-y-auto max-h-[500px] scrollbar-thin scrollbar-thumb-slate-200 space-y-2">
                  {renderAddStageSection('post')}
                  {formData.stages.post_casting.map((stage, idx) => (
                    <StageItem 
                      key={stage.stage_code} 
                      stage={stage} 
                      index={idx} 
                      total={formData.stages.post_casting.length} 
                      onMove={(i, dir) => moveStage('post', i, dir)} 
                      onToggle={() => toggleStage('post', idx)}
                      onUpdate={(data) => updateStage('post', idx, data)}
                      onDragStart={handleStageDragStart}
                      onDragOver={handleStageDragOver}
                      onDrop={handleStageDrop}
                      type="post"
                      availableMaterials={materialsList} // PASS MATERIALS LIST
                    />
                  ))}
                </div>
              </div>

              <div className="col-span-3 text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-2">
                <ArrowRight size={14} />
                Drag items to reorder. Uncheck stages to skip them for this specific design. Sequence determines the Job Card flow.
              </div>

            </div>
          )}

          {/* SECTION D: IMAGE */}
          {activeTab === 'image' && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 max-w-2xl mx-auto">
               <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                  <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600"><ImageIcon size={18}/></div>
                    Design Image & Description
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="text-sm font-semibold text-slate-700 block mb-2">Image Source</label>
                      
                      <div className="space-y-3">
                        {/* URL Input */}
                        <div className="relative">
                          <input 
                            type="text" 
                            name="image" 
                            value={formData.image && formData.image.startsWith('data:') ? '' : formData.image} 
                            onChange={handleChange}
                            placeholder="Paste image URL here..."
                            className="w-full pl-3 pr-10 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow shadow-sm text-sm"
                          />
                          {formData.image && formData.image.startsWith('data:') && (
                            <span className="absolute right-3 top-2.5 text-xs text-emerald-600 font-medium bg-emerald-50 px-2 rounded">Uploaded</span>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="h-px bg-slate-200 flex-1"></div>
                          <span className="text-xs text-slate-400 font-medium uppercase">OR</span>
                          <div className="h-px bg-slate-200 flex-1"></div>
                        </div>

                        {/* Drag & Drop Zone */}
                        <div 
                          className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center min-h-[120px] transition-all cursor-pointer ${
                            isDragging 
                              ? 'border-indigo-500 bg-indigo-50' 
                              : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
                          }`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={() => document.getElementById('image-upload').click()}
                        >
                          <input 
                            id="image-upload" 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={handleFileUpload}
                          />
                          <div className="flex flex-col items-center gap-2 text-slate-500 pointer-events-none">
                            <div className="p-3 bg-white rounded-full shadow-sm border border-slate-100">
                              <Upload size={20} className="text-indigo-600" />
                            </div>
                            <div className="text-center">
                              <span className="font-semibold text-indigo-600">Click to upload</span> or drag and drop
                            </div>
                            <p className="text-xs text-slate-400">SVG, PNG, JPG or GIF (max. 5MB)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-slate-700 block mb-2">Product Description</label>
                      <textarea
                        name="description"
                        value={formData.description || ''}
                        onChange={handleChange}
                        placeholder="Enter detailed product description..."
                        rows="4"
                        className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow shadow-sm resize-none"
                      />
                      <p className="text-xs text-slate-500 mt-1">This description will be displayed in the catalogue view.</p>
                    </div>

                    {/* Preview Section */}
                    {formData.image && (
                      <div className="border border-slate-200 rounded-xl p-2 bg-slate-50 relative group w-full max-w-sm mx-auto mt-4">
                        <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-white shadow-sm border border-slate-100">
                           <img 
                             src={formData.image} 
                             alt="Preview" 
                             className="w-full h-full object-contain"
                             onError={(e) => {e.target.style.display='none'; e.target.nextSibling.style.display='flex'}}
                           />
                           <div className="hidden absolute inset-0 flex-col items-center justify-center text-red-500 bg-slate-50">
                              <AlertCircle size={32} className="mb-2"/>
                              <span className="text-sm font-medium">Invalid Image Source</span>
                           </div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                          className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 shadow-md border border-white hover:bg-red-200 transition-colors"
                          title="Remove image"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>
               </div>
            </div>
          )}

        </div>
      </div>

      {nextTab && (
        <div className="border-t border-slate-200 bg-white px-6 py-4">
          <div className="mx-auto flex max-w-7xl justify-end">
            <button
              type="button"
              onClick={() => setActiveTab(nextTab.id)}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 font-medium text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md"
            >
              Next
              <span className="text-indigo-100">: {nextTab.shortLabel}</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {isStonePickerOpen && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-4xl rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Select Stone</h3>
                <p className="text-sm text-slate-500">Choose a stone from inventory to load its details into design master.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsStonePickerOpen(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">Stone Type</label>
                    <select
                      value={stoneTypeFilter}
                      onChange={(e) => setStoneTypeFilter(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    >
                      {stoneTypeMasterOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label || option.value}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">Size</label>
                    <select
                      value={stoneSizeFilter}
                      onChange={(e) => setStoneSizeFilter(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    >
                      <option value="">All Sizes</option>
                      {stoneSizeOptions.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">Shape</label>
                    <select
                      value={stoneShapeFilter}
                      onChange={(e) => setStoneShapeFilter(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    >
                      <option value="">All Shapes</option>
                      {stoneShapeOptions.map((shape) => (
                        <option key={shape} value={shape}>{shape}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-3 text-slate-400" size={16} />
                  <input
                    type="text"
                    value={stoneSearch}
                    onChange={(e) => setStoneSearch(e.target.value)}
                    placeholder="Search by stone name, type, size, shape, quality, clarity, cut, or color"
                    className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
              <div className="space-y-3">
                {filteredStoneOptions.map((stone) => (
                  <button
                    key={stone.material_id}
                    type="button"
                    onClick={() => handleSelectStone(stone)}
                    className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:border-indigo-300 hover:bg-indigo-50/40 hover:shadow-md"
                  >
                    <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)] lg:items-start">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Stone</div>
                        <div className="mt-1 text-base font-bold text-slate-900">{stone.name}</div>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Stone Type</div>
                            <div className="mt-1 font-medium text-slate-700">
                              {stone.stone_type ? String(stone.stone_type).replaceAll('_', ' ') : stone.material_type || 'Stone'}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Size</div>
                            <div className="mt-1 font-medium text-slate-700">{stone.size || 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                        {[
                          { label: 'Shape', value: stone.shape || 'N/A' },
                          { label: 'Quality', value: stone.quality_grade || 'Standard' },
                          { label: 'Clarity', value: stone.clarity || 'N/A' },
                          { label: 'Cut', value: stone.cut || 'N/A' },
                          { label: 'Color', value: stone.color || 'N/A' },
                        ].map((item) => (
                          <div key={`${stone.material_id}-${item.label}`} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{item.label}</div>
                            <div className="mt-1 break-words font-medium text-slate-700">{item.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </button>
                ))}
                {filteredStoneOptions.length === 0 && (
                  <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-400">
                    No stones found in inventory.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// --- MAIN APP COMPONENT ---

export default function App() {
  const [activeScreen, setActiveScreen] = useState('DESIGN_MASTER');
  const [designs, setDesigns] = useState([]);
  const [masterData, setMasterData] = useState({ categories: [], departments: [], customers: [], families: [], stones: [] });
  const [error, setError] = useState(null);
  // Add state for materials
  const [materialsList, setMaterialsList] = useState([]);

  const [viewMode, setViewMode] = useState('LIST');
  const [currentDesign, setCurrentDesign] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Mock navigation function as requested
  const navigate = (path) => {
    window.location.href = path;
  };

  // --- LOAD DESIGN DATA ---
  const loadDesignData = async () => {
      setIsLoading(true);
      setError(null);
      try {
          // 1. Simulate network delay so spinner is visible
          await sleep(1500);

          // 2. Fetch designs from real API
          const [desRes, matRes, masterRes, stoneRes] = await Promise.all([
            productionApi.get('/master-design/designs'),
            productionApi.get('/job-cards/materials-with-stock'),
            productionApi.get('/master-design/lookups'),
            productionApi.get('/inventory/stones'),
          ]);
          if (desRes.data && desRes.data.success) {
            setDesigns(desRes.data.data);
          }
          if (matRes.data && matRes.data.success) {
            setMaterialsList(matRes.data.data);
          }
          if (masterRes.data && masterRes.data.success) {
            setMasterData({
              ...masterRes.data.data,
              stones: stoneRes.data?.data || [],
            });
          }

      } catch (err) {
          console.error("Design master API failed:", err);
          setError("Failed to load design data. Please refresh.");
          setMasterData(null);
      } finally {
          setIsLoading(false);
      }
  };

  useEffect(() => {
    loadDesignData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveDesign = async (designData) => {
    setIsLoading(true);
    try {
        await sleep(1000); // UI feedback delay
        let response;
        if (designData.design_id) {
            response = await productionApi.put(`/master-design/designs/${designData.design_id}`, designData);
        } else {
            response = await productionApi.post('/master-design/designs', designData);
        }

        if (response.data && response.data.success) {
            // Refresh list
            const updatedList = await productionApi.get('/master-design/designs');
            if (updatedList.data && updatedList.data.success) {
                setDesigns(updatedList.data.data);
            }
            setViewMode('LIST');
        }
    } catch (error) {
        alert("Failed to save design: " + (error.response?.data?.message || error.message));
    } finally {
        setIsLoading(false);
    }
  };

  const handleEdit = async (design) => {
    setIsLoading(true);
    try {
        await sleep(500); // Small UI feedback delay
        const response = await productionApi.get(`/master-design/designs/${design.design_id}`);
        if (response.data && response.data.success) {
            const fetchedData = response.data.data;
            
            // Ensure nested stages structure exists
            if (!fetchedData.stages) fetchedData.stages = {};
            if (!fetchedData.stages.pre_casting) fetchedData.stages.pre_casting = [...DEFAULT_PRE_CASTING];
            if (!fetchedData.stages.casting) fetchedData.stages.casting = [...DEFAULT_CASTING];
            if (!fetchedData.stages.post_casting) fetchedData.stages.post_casting = [...DEFAULT_POST_CASTING];
            fetchedData.stages = sanitizeStageGroups(fetchedData.stages);

            setCurrentDesign(fetchedData);
            setViewMode('CREATE');
        }
    } catch (error) {
        console.error("Failed to fetch design details", error);
    } finally {
        setIsLoading(false);
    }
  };

  const handleView = async (design) => {
    setIsLoading(true);
    try {
        await sleep(500);
        const response = await productionApi.get(`/master-design/designs/${design.design_id}`);
        if (response.data && response.data.success) {
            setCurrentDesign(response.data.data);
            setViewMode('DETAIL');
        }
    } catch (error) {
        console.error("Failed to fetch design details", error);
    } finally {
        setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentDesign(null);
    setViewMode('CREATE');
  };

  const handleStatusChange = async (id, newStatus) => {
  try {
    const response = await productionApi.patch(
      `/master-design/designs/${id}/status`,
      { status: newStatus }
    );

    if (response.data?.success) {
      setDesigns(prev =>
        prev.map(d =>
          d.design_id === id
            ? { ...d, status: response.data.data.status }
            : d
        )
      );
    }
  } catch (error) {
    console.error("Failed to update status", error);
  }
};

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      
      {/* SIDEBAR NAVIGATION */}
      <aside className="w-64 bg-gradient-to-b from-indigo-900 via-blue-900 to-slate-900 text-white flex flex-col shadow-2xl z-20">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 bg-gradient-to-br from-amber-300 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-900/50">
               <Gem className="text-white drop-shadow-md" size={24} />
             </div>
             <div>
               <span className="text-xl font-black text-white tracking-tighter uppercase italic">
                 ZILVERRA<span className="text-indigo-400">JEWELS</span>
               </span>
             </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <div className="text-xs font-bold text-blue-300 uppercase px-3 mb-2 mt-4 tracking-wider">Core Modules</div>
          
          <button 
            onClick={() => { setActiveScreen('DESIGN_MASTER'); setViewMode('LIST'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
              activeScreen === 'DESIGN_MASTER' 
              ? 'bg-white/10 text-white shadow-inner backdrop-blur-sm border border-white/10' 
              : 'text-blue-200 hover:bg-white/5 hover:text-white'
            }`}
          >
            <Settings size={20} />
            <span className="font-medium">Design Master</span>
          </button>
          
          <button 
            onClick={() => { setActiveScreen('CATALOGUE'); setViewMode('LIST'); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
              activeScreen === 'CATALOGUE' 
              ? 'bg-white/10 text-white shadow-inner backdrop-blur-sm border border-white/10' 
              : 'text-blue-200 hover:bg-white/5 hover:text-white'
            }`}
          >
            <Grid3x3 size={20} />
            <span className="font-medium">Catalogue</span>
          </button>
        </nav>

        <div className="p-4 m-4 bg-black/20 rounded-xl border border-white/5 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center text-xs font-bold border-2 border-indigo-300/30">AD</div>
              <div>
                 <div className="text-sm font-medium text-white">Admin User</div>
                 <div className="text-xs text-blue-300">Production Mgr</div>
              </div>
            </div>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50/50">
        
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 shadow-sm z-10">
           <div className="flex items-center gap-4 text-slate-500 text-sm">
              <span 
                onClick={() => navigate("/production")} 
                className="hover:text-indigo-600 cursor-pointer transition-colors"
              >
                Home
              </span>
              <span className="text-slate-300">/</span>
              <span className="font-medium text-slate-800">
                {activeScreen === 'CATALOGUE' ? 'Catalogue' : 'Design Management'}
              </span>
           </div>
           <div className="flex items-center gap-4">
              <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold border border-emerald-100 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                System Online
              </span>
           </div>
        </header>

        {/* Screen Content */}
        <div className="flex-1 overflow-hidden p-6 relative">

          {error && masterData === null ? (
            <div className="text-center p-8">
              <AlertTriangle className="mx-auto text-red-500 mb-2" size={40}/>
              <p className="text-red-600 font-medium">Could not load design master data.</p>
              <button onClick={loadDesignData} className="mt-3 px-4 py-2 bg-blue-500 text-white rounded">
                Retry
              </button>
            </div>
          ) : activeScreen === 'DESIGN_MASTER' ? (
              viewMode === 'LIST' ? (
                <div className="animate-in fade-in duration-500 h-full">
                  <DesignList 
                    designs={designs} 
                    masterData={masterData}
                    onCreate={handleCreate} 
                    onEdit={handleEdit} 
                    onStatusChange={handleStatusChange}
                    isLoading={isLoading}
                  />
                </div>
              ) : (
                <div className="h-full animate-in slide-in-from-right-4 duration-500">
                  <DesignForm 
                    initialData={currentDesign} 
                    masterData={masterData}
                    materialsList={materialsList} // Pass materials here
                    isLoading={isLoading}
                    onSave={handleSaveDesign} 
                    onCancel={() => setViewMode('LIST')} 
                  />
                </div>
              )
          ) : activeScreen === 'CATALOGUE' ? (
              viewMode === 'LIST' ? (
               <div className="animate-in fade-in duration-500 h-full">
                  <DesignCatalogue designs={designs} masterData={masterData} onView={handleView} isLoading={isLoading} />
               </div>
              ) : (
                // Detail View
                <div className="h-full animate-in slide-in-from-right-4 duration-500">
                   <ProductDetailView 
                     design={currentDesign} 
                     masterData={masterData}
                     onBack={() => setViewMode('LIST')}
                     onEdit={handleEdit}
                   />
                </div>
              )
          ) : (
             <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                  <LayoutDashboard size={40} className="text-slate-300" />
                </div>
                <h3 className="text-xl font-medium text-slate-600">Dashboard View Placeholder</h3>
                <p className="mt-2 text-slate-500">Select "Design Master" from the sidebar to manage designs.</p>
             </div>
          )}

        </div>

      </main>
    </div>
  );
}
